// @steered AudibleSecurityContext 1.2 2026-09-21
import React, { useEffect, useState, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Row } from "antd"
import { FormField, PromptInput } from "@amzn/awsui-components-react/polaris";
import { LoadingBar } from "@amzn/awsui-chat-components";
import DOMPurify from 'dompurify';
import CBBTutorMessages from './CBBTutorMessages';
import CBBTutorLoading from './CBBTutorLoading';
import ConfigChecklist, { ConfigStatus } from './ConfigChecklist';
import { parseConfigFromResponse } from '../utils/parseConfigFromResponse';
import BrowserPreview from './BrowserPreview';
import AppetizePreview from './AppetizePreview';
import BuyBoxSidePanel from './BuyBoxSidePanel';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { lambdaService, LambdaError, LambdaErrorType, G2S2Result } from '../services/LambdaService';
import { getPreviewSurfaces, pickPreviewSurface, PREVIEW_SURFACE_LABELS, type PreviewSurface } from '../utils/previewSurfaces';
import { buildValidationUrl, buildProductPath, PageType } from '../utils/marketplaceDomains';
import { getSpecialResponse } from '../utils/uiHelpers';
import { extractWeblabId, validateWeblabForSession } from '../utils/weblabValidation';
import { WHAT_HAPPENS_NEXT } from '../utils/submissionProcess';
import SubmissionStatusCard from './SubmissionStatusCard';
import {
    SubmissionStatus, EMPTY_SUBMISSION_STATUS, LiveCheckResult,
    deriveSubmissionState, isPastPropagationGrace, applyLiveCheck,
} from '../utils/submissionStatus';
import './ChatComponents.css';

// Message interface is applied like so:
//    role: "user" | "assistant"
//    content: "I want to..."
interface Message {
    role: string;
    content: string;
}

interface DebugFeatures {
    browserPreview: boolean;
    showSessionIds: boolean;
}

interface UserSettings {
    darkMode: boolean;
    notificationSounds: boolean;
}



const extractNativeOverride = (validationUrl: string): string | null => {
    try {
        const override = new URL(validationUrl).searchParams.get('g2s2StageVersionOverride');
        return override?.replace(/^\{/, '').replace(/\}$/, '') || null;
    } catch {
        return null;
    }
};

interface G2S2ChatProps {
    debugFeatures?: DebugFeatures;
    userSettings?: UserSettings;
    tabColor?: string;
    tabName?: string;
    sessionId?: string;
}

const G2S2Chat = ({ debugFeatures, userSettings, tabColor, tabName, sessionId: tabSessionId }: G2S2ChatProps) => {
    // Initial message displayed by G2S2 Chat to greet the user
    const initMessage : Message = {
        role: "assistant",
        content: "Hey! I'm Lumi. I'll help you create and manage buy box configuration changes. To get started, include the following details in your message:\n\n" +
            "• **ASIN** — not required for record creation, but needed to generate the validation URL so you can preview your change (e.g., B08G9PRS1K)\n" +
            "• **ASIN Type** — audiobook, podcast, series, etc.\n" +
            "• **Customer Type** — member, non-member, or anonymous\n" +
            "• **Marketplace** — US, UK, DE, FR, JP (or full domain like amazon.com)\n" +
            "• **Page Type** — PDP, List View, or Amazon DP\n" +
            "• **Weblab:Treatment** — the weblab gating the change (e.g., MY_WEBLAB_123:T1)\n" +
            "• **LMS String ID** — the LMS string ID for the new text value (e.g., `audible_buybox_cta_free_trial`)\n\n" +
            "**Important:** In CBB, marketplace configuration is controlled by the weblab — where the weblab is dialed up determines which marketplaces see the change, so you don't need separate records per marketplace. However, a weblab can have multiple treatments (e.g., T1, T2, T3), and each treatment needs its own G2S2 record. Records may also vary by other key factors like customer type, ASIN type, or buying option. I can combine multiple records together in a single request.\n\n" +
            "**Example prompt:**\n\n" +
            "\"Change the cash CTA text to LMS string `audible_buybox_cta_free_trial` for ASIN `B08G9PRS1K`. This is an audiobook, targeting non-member customers in the US marketplace on the PDP. Gate it behind weblab `IGNITE_TEST_12345:T1`.\"\n\n" +
            "You can provide everything in one message like above, or give me details step by step. I'll track what's been set in the session checklist above.\n\n" +
            "Once the record is created, you'll get a preview to validate your changes (a browser preview for Audible web, a buy box side panel for Amazon DP, or an iOS/Android app preview for app changes) and an option to submit to Slack for team review.\n\n" +
            WHAT_HAPPENS_NEXT
    }
    
    // Storage key for this specific session
    const storageKey = `g2s2-chat-${tabSessionId}`;
    
    // Content hooks - initialize from localStorage
    const [allMessages, setAllMessages] = useState<Message[]>(() => {
        const saved = localStorage.getItem(`${storageKey}-messages`);
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Failed to parse saved messages', e);
            }
        }
        return [initMessage];
    });
    
    // The final user request sent to Claude after the user presses the submit button or "enter" key
    const [userMessageFinal, setUserMessageFinal] = useState("");
    // The message the user is currently drafting in the TextArea
    const [userMessageDraft, setUserMessageDraft] = useState("");
    // A flag that indicates whether the frontend has made all the necessary preparations to send the user's request to the backend
    const [readyToCallAPI, setReadyToCallAPI] = useState(false);
    // Stores Claude's response from the backend; is usually an array of the following format: [<Claude Response>, <Knowledge Base SessionID>]
    const [claudeResponse, setClaudeResponse] = useState<any>(null);
    // Stores the Knowledge Base SessionID
    const [sessionId, setSessionId] = useState<string | null>(() => {
        const saved = localStorage.getItem(`${storageKey}-kb-session`);
        return saved || null;
    });
    // Stores the user's model
    // 1 represents Claude v3 Sonnet, 2 represents Claude v3 Haiku
    // const [model, setModel] = useState(1);

    // Form state - initialize from localStorage
    const [showForm, setShowForm] = useState(() => {
        const saved = localStorage.getItem(`${storageKey}-show-form`);
        return saved ? saved === 'true' : false;
    });
    
    const [formData, setFormData] = useState(() => {
        const saved = localStorage.getItem(`${storageKey}-form-data`);
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Failed to parse saved form data', e);
            }
        }
        return {
            asin: '',
            asinType: '',
            customerType: '',
            country: '',
            pageType: ''
        };
    });

    // Hooks for UI/UX
    // A flag that indicates whether or not the user is waiting on a response from the backend
    const [loadingResponse, setLoadingResponse] = useState(false);
    
    // LLM-extracted session config for the checklist bar
    const EMPTY_SESSION_CONFIG: ConfigStatus = {
        asin: null, asinType: null, customerType: null,
        marketplace: null, pageType: null, weblabTreatment: null, clientId: null, applicationType: null,
    };
    const [sessionConfig, setSessionConfig] = useState<ConfigStatus>(() => {
        // Persisted so the preview surfaces (derived from clientId) survive a reload.
        const saved = localStorage.getItem(`${storageKey}-session-config`);
        if (saved) {
            try {
                return { ...EMPTY_SESSION_CONFIG, ...JSON.parse(saved) };
            } catch {
                /* fall through to empty */
            }
        }
        return EMPTY_SESSION_CONFIG;
    });
    useEffect(() => {
        localStorage.setItem(`${storageKey}-session-config`, JSON.stringify(sessionConfig));
    }, [sessionConfig, storageKey]);
    
    // Ref to always have the latest sessionConfig available synchronously
    // (React state updates are async, so sessionConfig may be stale inside invokeStrandsAgent)
    const sessionConfigRef = useRef(sessionConfig);
    useEffect(() => { sessionConfigRef.current = sessionConfig; }, [sessionConfig]);

    // What this session has written to G2S2 (label, stage version, exact records), as
    // reported by the Lambda from its session store. Drives the "is it live" check.
    const [g2s2Result, setG2S2Result] = useState<G2S2Result | null>(() => {
        const saved = localStorage.getItem(`${storageKey}-g2s2-result`);
        if (saved) {
            try { return JSON.parse(saved); } catch { /* fall through */ }
        }
        return null;
    });
    useEffect(() => {
        if (g2s2Result) localStorage.setItem(`${storageKey}-g2s2-result`, JSON.stringify(g2s2Result));
    }, [g2s2Result, storageKey]);

    // Submission lifecycle: ready -> in_review (submitted) -> live (verified against prod).
    const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>(() => {
        const saved = localStorage.getItem(`${storageKey}-submission-status`);
        if (saved) {
            try { return { ...EMPTY_SUBMISSION_STATUS, ...JSON.parse(saved) }; } catch { /* fall through */ }
        }
        return EMPTY_SUBMISSION_STATUS;
    });
    useEffect(() => {
        localStorage.setItem(`${storageKey}-submission-status`, JSON.stringify(submissionStatus));
    }, [submissionStatus, storageKey]);
    const submissionState = deriveSubmissionState(submissionStatus);
    const [checkingLive, setCheckingLive] = useState(false);
    const [lastLiveCheck, setLastLiveCheck] = useState<LiveCheckResult | null>(null);

    // The comparison against prod runs inside StrandsAgents (liveStatus action) using
    // the records it persisted for this session, so the browser needs nothing beyond
    // the session id and no access to G2S2HelperFunction.
    const runLiveCheck = React.useCallback(async () => {
        const sid = tabSessionId || sessionId;
        if (!sid || checkingLive) return;
        setCheckingLive(true);
        try {
            const result = await lambdaService.getSubmissionLiveStatus(sid);
            setLastLiveCheck(result);
            setSubmissionStatus(prev => applyLiveCheck(prev, result));
        } catch (error) {
            setLastLiveCheck({
                live: false, checked: 0, matched: 0, pending: [],
                error: error instanceof LambdaError ? error.getUserFriendlyMessage() : (error instanceof Error ? error.message : String(error)),
            });
        } finally {
            setCheckingLive(false);
        }
    }, [tabSessionId, sessionId, checkingLive]);

    // Check once when a submitted-but-not-live session is opened (past the G2S2
    // propagation window). No polling: deployment is hours to days away, and the
    // card has a button for anyone who wants to look again.
    const liveCheckOnOpenDone = useRef(false);
    useEffect(() => {
        if (liveCheckOnOpenDone.current) return;
        if (submissionState !== 'in_review' || !isPastPropagationGrace(submissionStatus)) return;
        liveCheckOnOpenDone.current = true;
        void runLiveCheck();
    }, [submissionState, submissionStatus, runLiveCheck]);
    
    // Slack submission state - Requirements: 4.1, 4.2, 4.6
    // Tracks whether the orchestrator has indicated a change is ready for submission
    const [isSlackSubmissionReady, setIsSlackSubmissionReady] = useState<boolean>(() => {
        const saved = localStorage.getItem(`${storageKey}-slack-ready`);
        return saved === 'true';
    });
    // Tracks whether a Slack submission is in progress
    const [isSubmittingToSlack, setIsSubmittingToSlack] = useState(false);
    // Stores the result of the Slack submission
    const [slackSubmissionResult, setSlackSubmissionResult] = useState<{success: boolean; message: string} | null>(null);
    // Stores the user's email from Cognito session
    const [userEmail, setUserEmail] = useState<string | null>(null);
    
    // Error handling state - Requirements: 2.5, 4.7, 6.1, 6.2, 6.3
    // Stores the current error for display in the UI
    const [currentError, setCurrentError] = useState<{
        type: LambdaErrorType;
        message: string;
        isRetryable: boolean;
        requiresReauth: boolean;
    } | null>(null);
    // Stores the last failed message for retry functionality
    const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
    
    // Browser validation state - Requirements: 5.1, 5.4, 5.5
    // Stores the validation URL for browser preview after successful record import
    const [validationUrl, setValidationUrl] = useState<string | null>(() => {
        const saved = localStorage.getItem(`${storageKey}-validation-url`);
        return saved || null;
    });
    // Controls whether the browser preview is shown for validation
    const [showValidationBrowser, setShowValidationBrowser] = useState<boolean>(() => {
        const saved = localStorage.getItem(`${storageKey}-show-validation`);
        return saved === 'true';
    });
    // Timer state for G2S2 propagation delay (15 minutes)
    const [validationTimerEnd, setValidationTimerEnd] = useState<number | null>(() => {
        const saved = localStorage.getItem(`${storageKey}-timer-end`);
        return saved ? parseInt(saved, 10) : null;
    });
    const [timerRemaining, setTimerRemaining] = useState<number>(0);
    const [timerNotificationEnabled, setTimerNotificationEnabled] = useState<boolean>(false);
    // Copy URL feedback state
    const [urlCopied, setUrlCopied] = useState<boolean>(false);
    // Preview surface and fullscreen state
    const [isBrowserFullscreen, setIsBrowserFullscreen] = useState<boolean>(false);
    const [previewSurface, setPreviewSurface] = useState<PreviewSurface>(() => {
        const saved = localStorage.getItem(`${storageKey}-preview-surface`);
        return saved === 'ios' || saved === 'android' ? saved : 'web';
    });
    
    // Session weblab ID - tracks the single weblab allowed per session
    const [sessionWeblabId, setSessionWeblabId] = useState<string | null>(() => {
        const saved = localStorage.getItem(`${storageKey}-weblab-id`);
        return saved || null;
    });
    
    // Slack submission metadata - weblab and description extracted from response
    const [slackWeblab, setSlackWeblab] = useState<string | null>(() => {
        const saved = localStorage.getItem(`${storageKey}-slack-weblab`);
        return saved || null;
    });
    const [slackDescription, setSlackDescription] = useState<string | null>(() => {
        const saved = localStorage.getItem(`${storageKey}-slack-description`);
        return saved || null;
    });
    
    // Save state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem(`${storageKey}-messages`, JSON.stringify(allMessages));
    }, [allMessages, storageKey]);
    
    useEffect(() => {
        localStorage.setItem(`${storageKey}-show-form`, showForm.toString());
    }, [showForm, storageKey]);
    
    useEffect(() => {
        localStorage.setItem(`${storageKey}-form-data`, JSON.stringify(formData));
    }, [formData, storageKey]);
    
    useEffect(() => {
        if (sessionId) {
            localStorage.setItem(`${storageKey}-kb-session`, sessionId);
        }
    }, [sessionId, storageKey]);
    
    // Persist Slack submission ready state
    useEffect(() => {
        localStorage.setItem(`${storageKey}-slack-ready`, isSlackSubmissionReady.toString());
    }, [isSlackSubmissionReady, storageKey]);
    
    // Persist Slack metadata (weblab and description)
    useEffect(() => {
        if (slackWeblab) {
            localStorage.setItem(`${storageKey}-slack-weblab`, slackWeblab);
        } else {
            localStorage.removeItem(`${storageKey}-slack-weblab`);
        }
    }, [slackWeblab, storageKey]);
    
    useEffect(() => {
        if (slackDescription) {
            localStorage.setItem(`${storageKey}-slack-description`, slackDescription);
        } else {
            localStorage.removeItem(`${storageKey}-slack-description`);
        }
    }, [slackDescription, storageKey]);
    
    // Persist session weblab ID
    useEffect(() => {
        if (sessionWeblabId) {
            localStorage.setItem(`${storageKey}-weblab-id`, sessionWeblabId);
        } else {
            localStorage.removeItem(`${storageKey}-weblab-id`);
        }
    }, [sessionWeblabId, storageKey]);
    
    // Persist validation browser state - Requirements: 5.1, 5.5
    useEffect(() => {
        if (validationUrl) {
            localStorage.setItem(`${storageKey}-validation-url`, validationUrl);
        } else {
            localStorage.removeItem(`${storageKey}-validation-url`);
        }
    }, [validationUrl, storageKey]);
    
    useEffect(() => {
        localStorage.setItem(`${storageKey}-show-validation`, showValidationBrowser.toString());
    }, [showValidationBrowser, storageKey]);

    useEffect(() => {
        localStorage.setItem(`${storageKey}-preview-surface`, previewSurface);
    }, [previewSurface, storageKey]);
    
    // Persist and manage the 15-minute timer for G2S2 propagation
    useEffect(() => {
        if (validationTimerEnd) {
            localStorage.setItem(`${storageKey}-timer-end`, validationTimerEnd.toString());
        } else {
            localStorage.removeItem(`${storageKey}-timer-end`);
        }
    }, [validationTimerEnd, storageKey]);
    
    // Fetch user email from Cognito session on mount
    // Requirements: 4.6 - Get user email from Cognito session
    useEffect(() => {
        const fetchUserEmail = async () => {
            try {
                const { idToken } = (await fetchAuthSession()).tokens ?? {};
                if (idToken?.payload?.email) {
                    setUserEmail(idToken.payload.email as string);
                }
            } catch (error) {
                console.error('Failed to fetch user email:', error);
            }
        };
        fetchUserEmail();
    }, []);
    
    // Ref for auto-scrolling to bottom
    const messagesEndRef = useRef<HTMLDivElement>(null);
    
    // Notification sound hook
    const { playSound } = useNotificationSound(userSettings?.notificationSounds ?? true);
    
    // Timer countdown effect - must be after playSound declaration
    useEffect(() => {
        if (!validationTimerEnd) {
            setTimerRemaining(0);
            return;
        }
        
        const updateTimer = () => {
            const now = Date.now();
            const remaining = Math.max(0, validationTimerEnd - now);
            setTimerRemaining(remaining);
            
            // Timer completed - send notification if enabled
            if (remaining === 0 && timerNotificationEnabled) {
                // Play notification sound
                playSound();
                
                // Show browser notification if permitted
                if (Notification.permission === 'granted') {
                    new Notification('G2S2 Changes Ready', {
                        body: 'Your G2S2 changes should now be propagated. Check the validation preview!',
                        icon: '/favicon.jpg'
                    });
                }
                
                // Add a message to the chat
                const timerCompleteMessage: Message = {
                    role: 'assistant',
                    content: '⏰ **15 minutes have passed!** Your G2S2 changes should now be propagated. Please refresh the validation preview to verify your changes are live.'
                };
                setAllMessages(prev => [...prev, timerCompleteMessage]);
                setTimerNotificationEnabled(false);
            }
        };
        
        // Update immediately
        updateTimer();
        
        // Update every second
        const interval = setInterval(updateTimer, 1000);
        
        return () => clearInterval(interval);
    }, [validationTimerEnd, timerNotificationEnabled, playSound]);
    
    // Toggle notification opt-in for the timer
    // Timer auto-starts when record is created, this just toggles notification
    const toggleTimerNotification = () => {
        const newState = !timerNotificationEnabled;
        setTimerNotificationEnabled(newState);
        
        // Request notification permission if enabling and not already granted
        if (newState && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    };
    
    // Format remaining time as MM:SS
    const formatTimeRemaining = (ms: number): string => {
        const totalSeconds = Math.ceil(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // BELOW: helper functions
    // Gets the Claude responses from the backend using direct Lambda invocation
    // Requirements: 2.1, 2.4, 3.1
    const invokeStrandsAgent = async () => {
        // Clear any previous errors before making a new request
        setCurrentError(null);
        
        try {
            // Use the tabSessionId passed from G2S2ChatContainer for session management
            // Requirements: 3.1 - Pass existing Session_ID to all Lambda invocations
            const response = await lambdaService.invokeStrandsAgent(userMessageFinal, tabSessionId || sessionId, userEmail || undefined);

            if (response) {
                // Debug: Log the raw response to understand its structure
                console.log('[G2S2Chat] Raw Lambda response:', JSON.stringify(response, null, 2));
                
                // Update the claudeResponse hook with the Lambda response
                // Response format: { response: string, sessionId: string, stageVersion?: string }
                setClaudeResponse([response.response, response.sessionId]);

                // Deterministic record of what was written (label/stage version/records) from the
                // session store; a later write supersedes an earlier one and resets the lifecycle.
                if (response.g2s2Result) {
                    setG2S2Result(prev => {
                        if (prev && prev.stageVersion !== response.g2s2Result!.stageVersion) {
                            setSubmissionStatus(EMPTY_SUBMISSION_STATUS);
                            setLastLiveCheck(null);
                        }
                        return response.g2s2Result!;
                    });
                }
                
                // Update session ID if returned from Lambda
                if (response.sessionId && response.sessionId !== sessionId) {
                    setSessionId(response.sessionId);
                }
                
                // Check if orchestrator indicates submission is ready
                // Requirements: 4.1 - Enable submission action when orchestrator indicates ready
                // Look for indicators in the response that suggest changes are ready for submission
                const responseText = typeof response.response === 'string' 
                    ? response.response.toLowerCase() 
                    : '';
                const originalResponseText = typeof response.response === 'string' 
                    ? response.response 
                    : '';
                
                // Extract weblab name and treatment from response for both Slack submission and validation URL
                // Primary source: sessionConfig (parsed from config block by parseConfigFromResponse)
                // Fallback: regex extraction from response text
                let extractedWeblab = sessionConfigRef.current.weblabTreatment || '';
                
                if (!extractedWeblab) {
                    let weblabName = '';
                    let treatmentCondition = '';
                    
                    // Match combined "Weblab/Treatment": "G2S2_1355070:T1" or "G2S2_1355070 : T1"
                    // Handle: **Weblab/Treatment:** value, **Weblab/Treatment** | value, Weblab/Treatment | value
                    const combinedMatch = originalResponseText.match(/\*\*Weblab\s*\/\s*Treatment:?\*\*:?[\s|]*([A-Z0-9_]+)\s*:\s*([CT][0-9]+)/i) ||
                                          originalResponseText.match(/Weblab\s*\/\s*Treatment[\s|:]+([A-Z0-9_]+)\s*:\s*([CT][0-9]+)/i);
                    if (combinedMatch) {
                        extractedWeblab = `${combinedMatch[1].trim()}:${combinedMatch[2].trim()}`;
                    } else {
                        // Match weblab name — require it to look like a weblab ID (contains underscore + digits)
                        // This avoids matching "no weblab gating" 
                        const weblabMatch = originalResponseText.match(/\*\*Weblab\*\*[:\s|]*([A-Z][A-Z0-9]*_[A-Z0-9_]+)/i) ||
                                            originalResponseText.match(/Weblab[:\s|]+([A-Z][A-Z0-9]*_[A-Z0-9_]+)/i);
                        if (weblabMatch) {
                            weblabName = weblabMatch[1].trim();
                        }
                        
                        // Match treatment condition
                        const treatmentMatch = originalResponseText.match(/\*\*Treatment(?:\s+Condition)?:?\*\*:?\s*([CT][0-9]+)/i) ||
                                               originalResponseText.match(/Treatment(?:\s+Condition)?[:\s|]+([CT][0-9]+)/i);
                        if (treatmentMatch) {
                            treatmentCondition = treatmentMatch[1].trim();
                        }
                        
                        if (weblabName) {
                            extractedWeblab = treatmentCondition 
                                ? `${weblabName}:${treatmentCondition}` 
                                : weblabName;
                        }
                    }
                }
                    
                if (response.stageVersion || 
                    responseText.includes('ready for submission') || 
                    responseText.includes('ready to submit') ||
                    responseText.includes('submit to slack') ||
                    responseText.includes('changes are ready') ||
                    responseText.includes('record created successfully') ||
                    responseText.includes('record successfully created') ||
                    responseText.includes('g2s2 record successfully created')) {
                    setIsSlackSubmissionReady(true);
                    
                    // Store extracted weblab for Slack submission
                    if (extractedWeblab) {
                        setSlackWeblab(extractedWeblab);
                        console.log('[G2S2Chat] Extracted weblab:', extractedWeblab);
                    }
                    
                    // Extract description - build from response context
                    // Look for "New CTA Text", "Target", customer type, marketplace info
                    let descriptionParts: string[] = [];
                    
                    // Extract target/what was modified - pattern like "**Target**: CASH button text for audiobooks"
                    const targetMatch = originalResponseText.match(/\*\*Target\*\*[:\s]*([^\n*]+)/i) ||
                                        originalResponseText.match(/Target[:\s]*([^\n]+)/i);
                    if (targetMatch) {
                        descriptionParts.push(`Modified ${targetMatch[1].trim()}`);
                    }
                    
                    // Extract new value if present - pattern like "**New CTA Text**: "g2s2_ignite_earth_test""
                    const newValueMatch = originalResponseText.match(/\*\*New CTA Text\*\*[:\s]*"?([^"\n]+)"?/i) ||
                                          originalResponseText.match(/New CTA Text[:\s]*"?([^"\n]+)"?/i);
                    if (newValueMatch) {
                        descriptionParts.push(`to '${newValueMatch[1].trim()}'`);
                    }
                    
                    // Add marketplace and customer type context from form data
                    if (formData.country) {
                        descriptionParts.push(`for ${formData.country.toUpperCase()}`);
                    }
                    if (formData.asinType) {
                        descriptionParts.push(formData.asinType);
                    }
                    if (formData.customerType) {
                        descriptionParts.push(formData.customerType);
                    }
                    
                    if (descriptionParts.length > 0) {
                        const description = descriptionParts.join(' ');
                        setSlackDescription(description);
                        console.log('[G2S2Chat] Built description:', description);
                    }
                }
                
                // Check for successful label/stage version creation for browser validation
                // Requirements: 5.1, 5.4 - Display browser preview after successful record import
                // Match patterns like "**Label**:", "record created successfully", "stage version"
                const hasRecordCreated = response.stageVersion || 
                    responseText.includes('label created') ||
                    responseText.includes('stage version created') ||
                    responseText.includes('record imported') ||
                    responseText.includes('successfully imported') ||
                    responseText.includes('record created successfully') ||
                    responseText.includes('successfully created') ||
                    responseText.includes('record created') ||
                    responseText.includes('**label**') ||
                    responseText.includes('**main label**') ||
                    responseText.includes('**production label**') ||
                    responseText.includes('**preview label**') ||
                    responseText.includes('**stage version**') ||
                    responseText.includes('igniteutilslabel') ||
                    responseText.includes('igniteutils-label');
                    
                if (hasRecordCreated) {
                    // Extract label name from response if available
                    // The label name might be in the stageVersion field or mentioned in the response
                    let labelName = response.stageVersion;
                    
                    // Try to extract label name from response text if not in stageVersion
                    // Look for patterns like: **Label**: `IgniteUtils-Label-xxx` or Label: IgniteUtils-Label-xxx
                    if (!labelName) {
                        // Match markdown format: **Label**: `value` or **Label:** `value` or **Label** | `value` (table row)
                        const markdownLabelMatch = originalResponseText.match(/\*\*(?:Main\s+|Production\s+)?Label:?\*\*[:\s|]*`?(IgniteUtils-Label-[a-f0-9-]+)`?/i);
                        if (markdownLabelMatch) {
                            labelName = markdownLabelMatch[1].trim();
                        }
                    }
                    
                    if (!labelName) {
                        // Direct match for IgniteUtils-Label-UUID pattern anywhere in text
                        const directMatch = originalResponseText.match(/(IgniteUtils-Label-[a-f0-9-]+)/i);
                        if (directMatch) {
                            labelName = directMatch[1];
                        }
                    }

                    if (!labelName) {
                        // Match plain format: Label: value or label: value (only if value looks like a label ID)
                        const plainLabelMatch = originalResponseText.match(/\blabel[:\s]+([a-zA-Z][a-zA-Z0-9_-]{8,})/i);
                        if (plainLabelMatch) {
                            labelName = plainLabelMatch[1].trim();
                        }
                    }
                    
                    console.log('[G2S2Chat] Detected record creation, extracted label:', labelName);
                    
                    if (labelName) {
                        // Parse config from this response immediately (sessionConfig hasn't been updated yet)
                        const { config: responseConfig } = parseConfigFromResponse(originalResponseText, sessionConfigRef.current);
                        const rawPageType = responseConfig.pageType || sessionConfigRef.current.pageType || 'pdp';
                        let inferredAsin = responseConfig.asin || sessionConfigRef.current.asin || '';
                        let inferredCountry = responseConfig.marketplace || sessionConfigRef.current.marketplace || 'us';
                        
                        // Fallback: extract ASIN from response text or conversation history
                        if (!inferredAsin) {
                            const fullText = allMessages.map(m => m.content).join(' ') + ' ' + originalResponseText;
                            const asinMatch = fullText.match(/\bB[0-9A-Z]{9}\b/);
                            if (asinMatch) {
                                inferredAsin = asinMatch[0];
                            }
                        }
                        
                        // Normalize page type to handle LLM variations like "Amazon DP", "AmazonDP", etc.
                        let finalPageType: string;
                        const lowerPageType = rawPageType.toLowerCase().replace(/[\s_]+/g, '-');
                        if (lowerPageType === 'amazon-dp' || lowerPageType === 'amazondp' || lowerPageType === 'amazon-detail-page') {
                            finalPageType = 'amazon-dp';
                        } else if (lowerPageType === 'pdp' || lowerPageType === 'audiblepdp' || lowerPageType === 'audible-pdp') {
                            finalPageType = 'pdp';
                        } else if (lowerPageType === 'list-view' || lowerPageType === 'listview' || lowerPageType === 'audiblelistview') {
                            finalPageType = 'list-view';
                        } else {
                            finalPageType = rawPageType;
                        }
                        
                        // For Amazon DP, ensure marketplace maps to amazon domain
                        if (finalPageType === 'amazon-dp' && !inferredCountry.startsWith('amazon-')) {
                            inferredCountry = `amazon-${inferredCountry}`;
                        }
                        
                        console.log('[G2S2Chat] URL construction - pageType:', finalPageType, 'country:', inferredCountry, 'asin:', inferredAsin);
                        
                        // Map pageType to ValidationUrlConfig pageType
                        let pageType: PageType;
                        if (finalPageType === 'amazon-dp') {
                            pageType = 'AmazonDP';
                        } else if (finalPageType === 'pdp') {
                            pageType = 'AudiblePDP';
                        } else {
                            pageType = 'AudibleListView';
                        }
                        
                        // For Amazon DP, use preview label if available
                        const previewLabelMatch = finalPageType === 'amazon-dp' 
                            ? originalResponseText.match(/\*\*Preview Label\*\*[:\s|]*`?(IgniteUtils-Label-[a-f0-9-]+)`?/i) ||
                              originalResponseText.match(/Preview Label[:\s|]*`?(IgniteUtils-Label-[a-f0-9-]+)`?/i) ||
                              originalResponseText.match(/preview.*?(IgniteUtils-Label-[a-f0-9-]+)/i)
                            : null;
                        const urlLabel = (previewLabelMatch ? previewLabelMatch[1].trim() : null) || labelName;
                        
                        // Build the validation URL
                        // Use weblab from this response's config block, falling back to earlier extracted value
                        const urlWeblab = responseConfig.weblabTreatment || extractedWeblab || undefined;
                        const validationConfig = {
                            pageType,
                            marketplace: inferredCountry.toUpperCase(),
                            labelName: urlLabel,
                            productPath: finalPageType === 'pdp' && inferredAsin 
                                ? buildProductPath('Product', inferredAsin) 
                                : undefined,
                            asin: inferredAsin || undefined,
                            keywords: finalPageType === 'list-view' 
                                ? inferredAsin || 'audiobook' 
                                : undefined,
                            weblab: finalPageType === 'amazon-dp' ? undefined : urlWeblab
                        };
                        
                        const url = buildValidationUrl(validationConfig);
                        setValidationUrl(url);
                        // Offer only the surfaces this record's clientId renders on.
                        const recordSurfaces = getPreviewSurfaces({
                            clientId: responseConfig.clientId || sessionConfigRef.current.clientId,
                            applicationType: responseConfig.applicationType || sessionConfigRef.current.applicationType,
                            nativePreviewSupported: finalPageType !== 'amazon-dp' && extractNativeOverride(url) !== null,
                        });
                        setPreviewSurface(recordSurfaces[0]);
                        setShowValidationBrowser(true);
                        
                        // Auto-start the 15-minute timer when record is created
                        // Timer starts immediately - user can opt-in to notification
                        if (!validationTimerEnd) {
                            const fifteenMinutes = 15 * 60 * 1000;
                            const endTime = Date.now() + fifteenMinutes;
                            setValidationTimerEnd(endTime);
                        }
                        
                        console.log('[G2S2Chat] Browser validation URL constructed:', url);
                    }
                }
                
                // Clear the last failed message on success
                setLastFailedMessage(null);
            } else {
                throw new Error('Failed to fetch response from Lambda');
            }
        } catch (error) {
            // Log any errors that occur
            console.error("Error invoking StrandsAgents Lambda:", error);
            
            // Store the failed message for retry functionality
            setLastFailedMessage(userMessageFinal);
            
            // Handle typed Lambda errors for better user feedback
            // Requirements: 2.5, 6.1, 6.2, 6.3 - Display user-friendly error message
            if (error instanceof LambdaError) {
                const userMessage = error.getUserFriendlyMessage();
                
                // Set the error state for the error banner UI
                setCurrentError({
                    type: error.type,
                    message: userMessage,
                    isRetryable: error.isRetryable,
                    requiresReauth: error.type === LambdaErrorType.AUTH_FAILURE
                });
                
                setClaudeResponse([userMessage, sessionId]);
            } else {
                // Generic error fallback
                setCurrentError({
                    type: LambdaErrorType.UNKNOWN,
                    message: "Something went wrong. Check to make sure you are using valid characters in your request, then try again.",
                    isRetryable: true,
                    requiresReauth: false
                });
                setClaudeResponse(["Something went wrong. Check to make sure you are using valid characters in your request, then try again.", sessionId]);
            }
        }
    }
    
    // Submit verified changes to Slack
    // Requirements: 4.1, 4.2, 4.6
    const submitToSlack = async () => {
        if (!tabSessionId && !sessionId) {
            setSlackSubmissionResult({
                success: false,
                message: 'No session ID available. Please start a conversation first.'
            });
            return;
        }
        
        setIsSubmittingToSlack(true);
        setSlackSubmissionResult(null);
        
        try {
            // Requirements: 4.2 - Invoke SlackWebhookFunction_Lambda with session ID
            // Requirements: 4.3 - Include Session_ID when invoking SlackWebhookFunction_Lambda
            const response = await lambdaService.invokeSlackWebhook(
                tabSessionId || sessionId!,
                userEmail || undefined,
                validationUrl || undefined, // Include validation URL for reviewers
                slackWeblab || undefined,   // Include weblab with treatment
                slackDescription || undefined // Include description of the change
            );
            
            // Requirements: 4.4 - Display confirmation message on success
            setSlackSubmissionResult({
                success: response.success,
                message: response.success 
                    ? 'Submitted! The ORCA team has been notified and will review your changes.'
                    : response.message || 'Submission failed. Please try again.'
            });
            
            // The card below the chat turns into the submission status; no chat message needed.
            if (response.success) {
                setSubmissionStatus(prev => ({ ...prev, submittedAt: prev.submittedAt ?? Date.now() }));
                // Reset submission ready state after successful submission
                setIsSlackSubmissionReady(false);
            }
        } catch (error) {
            console.error('Error submitting to Slack:', error);
            
            // Requirements: 4.5 - Display error and allow retry on failure
            let errorMessage = 'Failed to submit to Slack. Please try again.';
            if (error instanceof LambdaError) {
                errorMessage = error.getUserFriendlyMessage();
            }
            
            setSlackSubmissionResult({
                success: false,
                message: errorMessage
            });
        } finally {
            setIsSubmittingToSlack(false);
        }
    }

    // Preparations to send the user's request to the backend
    const handleSendText = () => {
        // Making sure the message the user wants to send isn't empty and is under the max of 1000 characters
        const userMessageLength = userMessageDraft.length;
        // Regex for input validation
        // Validates requests containing only letters, numbers, punctuation marks, and whitespace characters (including newlines and tabs), but no other special characters or control codes
        const regex = /^[\p{L}\p{N}\p{P}\p{Z}\r\n\t]+$/u;
        // If the message the user wants to send is valid
        if (userMessageLength > 0 && userMessageLength <= 1000 && regex.test(userMessageDraft)){
            const specialResponse = getSpecialResponse(userMessageDraft);
            if (specialResponse) {
                const userMsg: Message = { role: "user", content: userMessageDraft };
                const assistantMsg: Message = { role: "assistant", content: specialResponse };
                setAllMessages(prev => [...prev, userMsg, assistantMsg]);
                setUserMessageDraft("");
                return;
            }
            
            // Weblab guardrail: reject if user tries to use a different weblab ID
            const weblabError = validateWeblabForSession(userMessageDraft, sessionWeblabId);
            if (weblabError) {
                const userMsg: Message = { role: "user", content: userMessageDraft };
                const errorMsg: Message = { role: "assistant", content: `⚠️ ${weblabError}` };
                setAllMessages(prev => [...prev, userMsg, errorMsg]);
                setUserMessageDraft("");
                return;
            }
            
            // Track the weblab ID from this message if we don't have one yet
            const detectedWeblabId = extractWeblabId(userMessageDraft);
            if (detectedWeblabId && !sessionWeblabId) {
                setSessionWeblabId(detectedWeblabId);
            }
            
            console.log("start: " + new Date().getTime());
            // Set the final user message with a sanitized version of the user's draft
            setUserMessageFinal(sanitizeInput(userMessageDraft));
            // Indicate that we are ready to call the API
            setReadyToCallAPI(true);
            // Reset Slack submission result when user sends a new message
            // This re-enables the Submit button if they want to submit again after making more changes
            setSlackSubmissionResult(null);
        }
        else if (userMessageLength > 1000){
            setClaudeResponse(["Make sure your request is below 1000 characters.", sessionId])
        }
        else{
            setClaudeResponse(["Make sure your request includes only letters, numbers, punctuation marks, and whitespace characters.", sessionId])
        }
    }

    const handleDisplayUserRequest= () => {
        // If the message the user wants to send is valid (has actual content), then format it and add it into the conversation history.
        // Also, clear the userMessageDraft so nothing shows up in the TextArea when the user sends a valid message
        if (userMessageFinal.length > 0 && readyToCallAPI){
            // Package the user's request into an interface object for easier rendering logic later on
            const newMessage : Message = {
                role : "user",
                content : userMessageFinal
            }
            // Incorporate the newMessage into allMessages (the messages rendered on the screen)
            setAllMessages([...allMessages, newMessage]);
            // Clear the user's draft so that nothing shows up in the TextArea
            setUserMessageDraft("");
        }
    }

    // Handles the Claude response to extract relevant information from it
    const handleClaudeResponse = () => {
        // Claude usually outputs whitespace characters that don't render properly, so clean the response up
        const cleanedResponse = cleanClaudeResponse();
        
        // Parse and strip the structured config block from the response
        const { config: newConfig, cleanedResponse: displayResponse } = parseConfigFromResponse(cleanedResponse, sessionConfig);
        setSessionConfig(newConfig);
        
        // Format the Claude response for better rendering logic
        const claudeResponseFormatted : Message = {
            role : "assistant",
            content: displayResponse
        }
        // Incorporate Claude's responses into allMessages for display to the user
        setAllMessages([...allMessages, claudeResponseFormatted]);
        // If this was the first response we got from Claude during this conversation, then set the sessionId hook with
        // the sessionId returned from the Knowledge Base
        if (sessionId == null){
            setSessionId(claudeResponse[1]);
        }
        
        // Play notification sound if enabled and tab is inactive
        playSound();
        console.log("end : " + new Date().getTime());
    }

    // Claude's responses are formatted with \n, /, and other characters
    // that should be cleaned before display to the user
    const cleanClaudeResponse = () => {
        const regex = /\\n|\\"|[\u0000-\u001F\u007F-\u009F\u2000-\u206F\u2E00-\u2E7F\u3000-\u303F\uD800-\uDFFF]/gu;
        const replacements: { [key: string]: string } = {
            '\\n': '\n',
            '\\"': '',
        };
        const fixContent = JSON.stringify(claudeResponse[0]);
        // Remove the wrapping quotes from JSON.stringify
        const unwrapped = fixContent.slice(1, -1);
        const cleanedContent = unwrapped.replace(regex, (match) => {
            if (match in replacements) {
                return replacements[match as keyof typeof replacements];
            } else {
                return '';
            }
        });
        return cleanedContent;
    };

    // Sanitize user input using DOMPurify
    function sanitizeInput(input : string) {
        const sanitizedInput = DOMPurify.sanitize(input);
        return sanitizedInput;
    }

    // Handle form submission
    const handleFormSubmit = () => {
        if (formData.asin && formData.asinType && formData.customerType && formData.country && formData.pageType) {
            setShowForm(false);
            // Log session information
            console.log('G2S2 Session Started:', {
                sessionId: tabSessionId,
                tabName: tabName,
                formData: formData,
                timestamp: new Date().toISOString()
            });
            
            // Send initial context message to the Lambda with form data
            // This provides the orchestrator with the configuration context
            const contextMessage = `I'm starting a new G2S2 change session with the following configuration:
- Example ASIN: ${formData.asin}
- ASIN Type: ${formData.asinType}
- Customer Type: ${formData.customerType}
- Country/Marketplace: ${formData.country}
- Page Type: ${formData.pageType}

Please acknowledge this configuration and let me know what kind of changes I can make.`;
            
            // Set the context message as the first user message to send
            // The handleDisplayUserRequest will add it to allMessages when readyToCallAPI is true
            setUserMessageFinal(contextMessage);
            setReadyToCallAPI(true);
            setLoadingResponse(true);
        }
    };
    
    // Handle retry for failed requests
    // Requirements: 6.3 - Offer retry for retryable errors
    const handleRetry = () => {
        if (lastFailedMessage) {
            setCurrentError(null);
            setUserMessageFinal(lastFailedMessage);
            setReadyToCallAPI(true);
        }
    };
    
    // Handle re-authentication for auth failures
    // Requirements: 6.2 - Prompt user to re-authenticate
    const handleReauthenticate = async () => {
        try {
            // Force refresh the session to get new credentials
            await fetchAuthSession({ forceRefresh: true });
            setCurrentError(null);
            // Retry the last failed message if available
            if (lastFailedMessage) {
                handleRetry();
            }
        } catch (error) {
            console.error('Re-authentication failed:', error);
            // If re-auth fails, user may need to sign in again
            setCurrentError({
                type: LambdaErrorType.AUTH_FAILURE,
                message: 'Re-authentication failed. Please sign out and sign in again.',
                isRetryable: false,
                requiresReauth: true
            });
        }
    };
    
    // Dismiss the error banner
    const dismissError = () => {
        setCurrentError(null);
    };

    // BELOW: useEffect hooks related to getting information to and from the backend
    useEffect(() => {
        // If the user wants to send a message, display it on the screen
        handleDisplayUserRequest();
    }, [userMessageFinal])

    useEffect(() => {
        // If the conversation history changes due to a user request (in other words, the user has sent a request),
        // then make a call to the backend and set the loading status to true
        if (allMessages.length > 0 && readyToCallAPI){
            // We aren't ready to call the backend API because we are processing a request right now
            setReadyToCallAPI(false);
            // Process the user request using direct Lambda invocation
            // Requirements: 2.1 - Invoke StrandsAgents_Lambda with message payload
            invokeStrandsAgent();
            // Set this flag to true because we are now waiting for a response from Claude
            setLoadingResponse(true);
        }
    }, [allMessages])

    useEffect(() => {
        // If the backend returns a valid Claude response that changes the
        // claudeResponse hook, then incorporate it properly into our
        // conversation history (allMessages hook) for rendering
        if (claudeResponse !== null && claudeResponse !== undefined && claudeResponse[0] !== undefined
            && claudeResponse[0] !== null && claudeResponse[0].length > 0 ){
            // Incorporate Claude's response properly and extract relevant information from it
            handleClaudeResponse();
            // Set this flag to false because we are no longer waiting on a response
            setLoadingResponse(false);
            // Clear the user's final message because it has already been processed
            setUserMessageFinal("");
        }
    }, [claudeResponse])

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [allMessages, loadingResponse]);

    const nativeOverrideLabel = validationUrl ? extractNativeOverride(validationUrl) : null;
    const nativePreviewSupported = formData.pageType !== 'amazon-dp' && nativeOverrideLabel !== null;
    // Surfaces are derived from the record's clientId, so a web-only change
    // (AudiblePDP / AudibleListView) never offers iOS/Android tabs and a native
    // change (AudibleApp_*) never offers a web page that does not render it.
    const availablePreviewSurfaces = getPreviewSurfaces({
        clientId: sessionConfig.clientId,
        applicationType: sessionConfig.applicationType,
        nativePreviewSupported,
    });
    const activePreviewSurface = pickPreviewSurface(previewSurface, availablePreviewSurfaces);
    const nativePreviewAvailable = nativePreviewSupported && activePreviewSurface !== 'web';
    useEffect(() => {
        if (activePreviewSurface !== previewSurface) {
            setPreviewSurface(activePreviewSurface);
        }
    }, [activePreviewSurface, previewSurface]);

    return (
        <div className="chat-with-browser-container">
            <div className={`chat-container ${isBrowserFullscreen ? 'hidden-for-fullscreen' : ''}`}>
                
                {!showForm && debugFeatures?.showSessionIds && (
                    <div className="g2s2-session-info-bar">
                        <span className="g2s2-session-label">Session ID:</span>
                        <code className="g2s2-session-code" title="Click to copy">
                            {tabSessionId}
                        </code>
                    </div>
                )}
                
                {/* Error Banner - Requirements: 2.5, 4.7, 6.1, 6.2, 6.3 */}
                {currentError && !showForm && (
                    <div className={`g2s2-error-banner ${currentError.type.toLowerCase()}`}>
                        <div className="g2s2-error-content">
                            <span className="g2s2-error-icon">
                                {currentError.requiresReauth ? '🔐' : currentError.isRetryable ? '⚠️' : '❌'}
                            </span>
                            <div className="g2s2-error-text">
                                <span className="g2s2-error-message">{currentError.message}</span>
                            </div>
                        </div>
                        <div className="g2s2-error-actions">
                            {currentError.requiresReauth && (
                                <button 
                                    className="g2s2-error-button reauth"
                                    onClick={handleReauthenticate}
                                >
                                    Sign In Again
                                </button>
                            )}
                            {currentError.isRetryable && lastFailedMessage && (
                                <button 
                                    className="g2s2-error-button retry"
                                    onClick={handleRetry}
                                >
                                    Retry
                                </button>
                            )}
                            <button 
                                className="g2s2-error-button dismiss"
                                onClick={dismissError}
                                aria-label="Dismiss error"
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                )}

                {showForm ? (
                    <div className="g2s2-form-container">
                        <div className="g2s2-form-card">
                            <h2 className="g2s2-form-title">G2S2 Configuration</h2>
                            <p className="g2s2-form-description">
                                Please fill out the information below to begin your chat session.
                            </p>
                            <p className="g2s2-form-disclaimer">
                                ℹ️ This tool works for any Audible domain (audible.com, audible.co.uk, etc.)
                            </p>
                            
                            <div className="g2s2-form-fields">
                                <div className="g2s2-form-field">
                                    <label className="g2s2-form-label">Example ASIN</label>
                                    <input
                                        type="text"
                                        className="g2s2-form-input"
                                        placeholder="e.g., B08G9PRS1K"
                                        value={formData.asin}
                                        onChange={(e) => setFormData({...formData, asin: e.target.value})}
                                    />
                                </div>

                                <div className="g2s2-form-field">
                                    <label className="g2s2-form-label">ASIN Type</label>
                                    <select
                                        className="g2s2-form-select"
                                        value={formData.asinType}
                                        onChange={(e) => setFormData({...formData, asinType: e.target.value})}
                                    >
                                        <option value="">Select ASIN type...</option>
                                        <option value="audiobook">Audiobook</option>
                                        <option value="audiobook-aycl">Audiobook AYCL</option>
                                        <option value="audiobook-preorder">Audiobook Preorder</option>
                                        <option value="audiobook-aycl-preorder">Audiobook AYCL Preorder</option>
                                        <option value="podcast">Podcast</option>
                                    </select>
                                </div>

                                <div className="g2s2-form-field">
                                    <label className="g2s2-form-label">Customer Type</label>
                                    <select
                                        className="g2s2-form-select"
                                        value={formData.customerType}
                                        onChange={(e) => setFormData({...formData, customerType: e.target.value})}
                                    >
                                        <option value="">Select customer type...</option>
                                        <option value="member">Member</option>
                                        <option value="non-member">Non-Member</option>
                                        <option value="anonymous">Anonymous</option>
                                    </select>
                                </div>

                                <div className="g2s2-form-field">
                                    <label className="g2s2-form-label">Page Type</label>
                                    <select
                                        className="g2s2-form-select"
                                        value={formData.pageType}
                                        onChange={(e) => setFormData({...formData, pageType: e.target.value, country: ''})}
                                    >
                                        <option value="">Select page type...</option>
                                        <option value="pdp">Product Detail Page (PDP)</option>
                                        <option value="list-view">List View</option>
                                        <option value="amazon-dp">Amazon Product Page (DP)</option>
                                    </select>
                                </div>

                                <div className="g2s2-form-field">
                                    <label className="g2s2-form-label">Country</label>
                                    <select
                                        className="g2s2-form-select"
                                        value={formData.country}
                                        onChange={(e) => setFormData({...formData, country: e.target.value})}
                                    >
                                        <option value="">Select country...</option>
                                        {formData.pageType === 'amazon-dp' ? (
                                            <>
                                                <option value="amazon-us">United States (amazon.com)</option>
                                                <option value="amazon-uk">United Kingdom (amazon.co.uk)</option>
                                                <option value="amazon-ca">Canada (amazon.ca)</option>
                                                <option value="amazon-au">Australia (amazon.com.au)</option>
                                                <option value="amazon-de">Germany (amazon.de)</option>
                                                <option value="amazon-fr">France (amazon.fr)</option>
                                                <option value="amazon-it">Italy (amazon.it)</option>
                                                <option value="amazon-es">Spain (amazon.es)</option>
                                                <option value="amazon-in">India (amazon.in)</option>
                                                <option value="amazon-jp">Japan (amazon.co.jp)</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value="us">United States (audible.com)</option>
                                                <option value="uk">United Kingdom (audible.co.uk)</option>
                                                <option value="ca">Canada (audible.ca)</option>
                                                <option value="au">Australia (audible.com.au)</option>
                                                <option value="de">Germany (audible.de)</option>
                                                <option value="fr">France (audible.fr)</option>
                                                <option value="it">Italy (audible.it)</option>
                                                <option value="es">Spain (audible.es)</option>
                                                <option value="in">India (audible.in)</option>
                                                <option value="jp">Japan (audible.co.jp)</option>
                                            </>
                                        )}
                                    </select>
                                </div>

                                <button
                                    className="g2s2-form-submit"
                                    onClick={handleFormSubmit}
                                    disabled={!formData.asin || !formData.asinType || !formData.customerType || !formData.country || !formData.pageType}
                                >
                                    Start Chat
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="chat-top-header">
                            <span className="chat-top-header-title">{tabName || 'QuickLab'}</span>
                        </div>
                        <div className="chat-messages-wrapper">
                            <Row>
                                {allMessages.map((message, index) => (
                                    <CBBTutorMessages 
                                        key={index} 
                                        index={index} 
                                        message={message}
                                        userInitial={userEmail ? userEmail.charAt(0) : 'U'}
                                    />
                                ))}
                                {loadingResponse && <CBBTutorLoading/>}
                                <div ref={messagesEndRef} />
                            </Row>
                        </div>
                        
                        {/* Slack Submission Section - Requirements: 4.1, 4.2, 4.6 */}
                        {/* Show when validation browser is visible (user can review and submit) */}
                        {showValidationBrowser && validationUrl && (
                            <div className="g2s2-slack-submission-container">
                                <SubmissionStatusCard
                                    state={submissionState}
                                    status={submissionStatus}
                                    canCheck={!lastLiveCheck?.noRecords}
                                    checking={checkingLive}
                                    lastCheck={lastLiveCheck}
                                    onCheckStatus={() => { void runLiveCheck(); }}
                                    onSubmit={submitToSlack}
                                    submitDisabled={isSubmittingToSlack || loadingResponse || !!slackSubmissionResult?.success}
                                    submitLabel={isSubmittingToSlack ? 'Submitting...' : slackSubmissionResult?.success ? '✓ Submitted' : 'Submit to Slack for Review'}
                                >
                                    {availablePreviewSurfaces.length > 1 && (
                                        <div className="g2s2-preview-surface-selector" role="group" aria-label="Preview surface">
                                            <span className="g2s2-preview-surface-label">Preview</span>
                                            {availablePreviewSurfaces.map((surface) => (
                                                <button
                                                    key={surface}
                                                    className={`g2s2-preview-surface-button ${activePreviewSurface === surface ? 'selected' : ''}`}
                                                    onClick={() => setPreviewSurface(surface)}
                                                    type="button"
                                                    aria-pressed={activePreviewSurface === surface}
                                                >
                                                    {PREVIEW_SURFACE_LABELS[surface]}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {availablePreviewSurfaces.length === 1 && formData.pageType !== 'amazon-dp' && (
                                        <p className="g2s2-preview-surface-note" data-testid="preview-surface-note">
                                            Preview: {PREVIEW_SURFACE_LABELS[availablePreviewSurfaces[0]]}
                                            {sessionConfig.clientId ? ` (${sessionConfig.clientId})` : ''}
                                        </p>
                                    )}
                                    {slackSubmissionResult && !slackSubmissionResult.success && (
                                        <div className="g2s2-slack-result error">
                                            {slackSubmissionResult.message}
                                        </div>
                                    )}
                                </SubmissionStatusCard>
                            </div>
                        )}

                        <div className="chat-input-container">
                            <div className="modern-input-wrapper">
                                <FormField
                                    stretch={true}
                                    constraintText={
                                        <>Character count: {userMessageDraft.length} / 1000</>
                                    }
                                    errorText={
                                        userMessageDraft.length > 1000 &&
                                        "The prompt has too many characters."
                                    }
                                >
                                    {loadingResponse ? (
                                        <div className="chat-loading-bar">
                                            <LoadingBar variant="gen-ai-masked" />
                                        </div>
                                    ) : (
                                        <PromptInput
                                            onChange={({ detail }) => setUserMessageDraft(detail.value)}
                                            onAction={handleSendText}
                                            value={userMessageDraft}
                                            actionButtonAriaLabel="Send message"
                                            actionButtonIconName="angle-up"
                                            placeholder="Ask anything..."
                                            spellcheck
                                            minRows={1}
                                            maxRows={20}
                                            disableActionButton={userMessageDraft.length === 0}
                                        />
                                    )}
                                </FormField>
                            </div>
                        </div>
                    </>
                )}
            </div>
            
            {/* Browser Preview - Show validation browser after successful import, or debug browser */}
            {/* Requirements: 5.1, 5.5 - Display inline browser for validation after record import */}
            {/* For Amazon DP, show BuyBoxSidePanel instead of iframe BrowserPreview */}
            {showValidationBrowser && validationUrl && formData.pageType === 'amazon-dp' && (
                    <BuyBoxSidePanel
                        tabColor={tabColor || '#CC0C39'}
                        tabName={tabName || "Buy Box Preview"}
                        asinType={formData.asinType}
                        asin={formData.asin}
                        validationInfo={{
                            timerEndTime: validationTimerEnd,
                            timerRemaining: timerRemaining,
                            notificationEnabled: timerNotificationEnabled,
                            onToggleNotification: toggleTimerNotification,
                            onCopyUrl: () => {
                                navigator.clipboard.writeText(validationUrl);
                                setUrlCopied(true);
                                setTimeout(() => setUrlCopied(false), 2000);
                            },
                            urlCopied: urlCopied,
                            validationUrl: validationUrl
                        }}
                        onFullscreenChange={setIsBrowserFullscreen}
                    />
            )}
            {(showValidationBrowser && validationUrl && formData.pageType !== 'amazon-dp' && activePreviewSurface === 'web') && (
                    <BrowserPreview 
                        tabColor={tabColor} 
                        tabName={tabName || "Validation Preview"} 
                        initialUrl={validationUrl}
                        validationInfo={{
                            customerType: formData.customerType,
                            timerEndTime: validationTimerEnd,
                            timerRemaining: timerRemaining,
                            notificationEnabled: timerNotificationEnabled,
                            onToggleNotification: toggleTimerNotification,
                            onCopyUrl: () => {
                                navigator.clipboard.writeText(validationUrl);
                                setUrlCopied(true);
                                setTimeout(() => setUrlCopied(false), 2000);
                            },
                            urlCopied: urlCopied
                        }}
                        onFullscreenChange={setIsBrowserFullscreen}
                    />
            )}
            {showValidationBrowser && nativePreviewAvailable && (
                <AppetizePreview
                    key={activePreviewSurface}
                    platform={activePreviewSurface}
                    overrideLabel={nativeOverrideLabel!}
                    weblab={slackWeblab || sessionConfig.weblabTreatment || undefined}
                    tabColor={tabColor}
                    tabName={`${activePreviewSurface === 'ios' ? 'iOS' : 'Android'} App Preview`}
                    onFullscreenChange={setIsBrowserFullscreen}
                />
            )}
            {debugFeatures?.browserPreview && !showValidationBrowser && (
                <BrowserPreview tabColor={tabColor} tabName={tabName} onFullscreenChange={setIsBrowserFullscreen} />
            )}
        </div>
    )
}

export default G2S2Chat;